import { imageIdOf, plainRefs, slugRef, type Entity, type StoryCtx, type StoryNode } from '@dispositio/core';
import { refOptions } from './refPicker';

/** Ein Eintrag im Referenz-Browser. */
export interface BrowseItem {
  entityId: string;
  /** Schlüssel, der eingefügt wird: der Hauptschlüssel, bei Suche nach einer weiteren Bezeichnung („clo“ → „cloe“) diese */
  refKey: string;
  title: string;
  typeId: string;
  typeName: string;
  typeColor: string;
  imageId: string | null;
  /** Anfang der Kurzbeschreibung */
  text: string;
  /** Kommt in der Szene des Textes vor */
  near: boolean;
  /** Wie gut der Eintrag zur Markierung passt (0 = gar nicht, 3 = genau) */
  score: number;
}

export type BrowseSectionKey = 'favorites' | 'match' | 'near' | 'all';

export interface BrowseResult {
  sections: { key: BrowseSectionKey; items: BrowseItem[] }[];
  /** Klassen mit Einträgen (zum Filtern), mit Anzahl */
  classes: { id: string; name: string; color: string; count: number }[];
}

const words = (s: string): string[] =>
  s
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((w) => w.length >= 3);

/** Passt der Eintrag zur Markierung? Genau (Name oder Schlüssel), ein ganzes Wort gleich, oder ein Wortanfang („Sarahs“ ↔ „Sarah“). */
export function matchScore(selection: string, title: string, keys: string[]): number {
  const sel = selection.trim().toLowerCase();
  if (sel === '') return 0;
  if (title.toLowerCase() === sel || keys.includes(slugRef(selection))) return 3;
  const sw = words(sel);
  const tw = words(title);
  const kw = keys.flatMap((k) => k.split('_')).filter((k) => k.length >= 3);
  if (sw.some((w) => tw.includes(w) || kw.includes(w))) return 2;
  if (sw.some((w) => [...tw, ...kw].some((t) => (t.startsWith(w) || w.startsWith(t)) && Math.min(t.length, w.length) >= 4))) return 1;
  return 0;
}

/**
 * Einträge für den Referenz-Browser: ganz oben die Favoriten (`favorites`, IDs der Einträge – z. B. die, die im Dokument schon
 * vorkommen; leer: kein solcher Abschnitt), darunter ohne Suche drei Abschnitte (passt zur Markierung, in dieser Szene, alle übrigen),
 * mit Suche eine Liste der Treffer. Innerhalb eines Abschnitts stehen die zur Markierung passenden zuerst (Favoriten danach in ihrer
 * Reihenfolge); kein Eintrag steht doppelt. `typeId` filtert nach Klasse.
 */
export function browseRefs(ctx: StoryCtx, opts: { text: string; query?: string; typeId?: string | null; node?: StoryNode; favorites?: string[] }): BrowseResult {
  const q = (opts.query ?? '').trim().toLowerCase();
  const all: BrowseItem[] = refOptions(ctx, opts.node).map((o) => {
    const entity = ctx.entities.get(o.entityId)!;
    const type = ctx.types.get(entity.typeId)!;
    const hover = type.hoverKey ? plainRefs(String(entity.fields[type.hoverKey] ?? '')).replace(/\s+/g, ' ').trim() : '';
    const key = q === '' ? entity.refKey : (o.keys.find((k) => k.startsWith(q)) ?? o.keys.find((k) => k.includes(q)) ?? entity.refKey);
    return {
      entityId: o.entityId,
      refKey: key,
      title: o.title,
      typeId: o.typeId,
      typeName: o.typeName,
      typeColor: o.typeColor,
      imageId: imageIdOf(type, entity.fields, entity.colorSlot, entity.id),
      text: hover.length > 70 ? `${hover.slice(0, 68)}…` : hover,
      // Welt und Prämisse gelten überall; sie stehen nicht bei der Szene, sondern am Ende
      near: o.near && type.category !== 'foundational',
      // ein Vorkommen des Elements („Clärchen“) passt so gut wie sein Name
      score: Math.max(matchScore(opts.text, o.title, o.keys), (entity.terms ?? []).some((t) => t.toLowerCase() === opts.text.trim().toLowerCase()) ? 3 : 0),
    };
  });
  const last = (it: BrowseItem): number => (ctx.types.get(it.typeId)?.category === 'foundational' ? 1 : 0);
  const found = (e: Entity): boolean => e.refKey.includes(q) || (e.aliases ?? []).some((a) => a.includes(q)) || (e.terms ?? []).some((t) => t.toLowerCase().includes(q));
  const searched = q === '' ? all : all.filter((it) => it.title.toLowerCase().includes(q) || it.typeName.toLowerCase().includes(q) || found(ctx.entities.get(it.entityId)!));

  const counts = new Map<string, number>();
  for (const it of searched) counts.set(it.typeId, (counts.get(it.typeId) ?? 0) + 1);
  const classes = [...counts.entries()]
    .map(([id, count]) => ({ id, count, type: ctx.types.get(id)! }))
    .sort((a, b) => Number(a.type.category === 'foundational') - Number(b.type.category === 'foundational') || a.type.sort - b.type.sort)
    .map(({ id, count, type }) => ({ id, name: type.name, color: type.color, count }));
  const filtered = opts.typeId ? searched.filter((it) => it.typeId === opts.typeId) : searched;
  const byScore = (a: BrowseItem, b: BrowseItem): number => b.score - a.score;
  const favRank = new Map((opts.favorites ?? []).map((id, i) => [id, i]));
  const favorites = filtered.filter((it) => favRank.has(it.entityId)).sort((a, b) => byScore(a, b) || favRank.get(a.entityId)! - favRank.get(b.entityId)!);
  const shown = filtered.filter((it) => !favRank.has(it.entityId));
  const fav = favRank.size > 0 ? [{ key: 'favorites' as const, items: favorites }] : [];
  if (q !== '') return { sections: [...fav, { key: 'all', items: [...shown].sort(byScore) }], classes };
  const match = shown.filter((it) => it.score > 0).sort(byScore);
  const near = shown.filter((it) => it.score === 0 && it.near);
  const rest = shown.filter((it) => it.score === 0 && !it.near).sort((a, b) => last(a) - last(b));
  return {
    sections: [
      ...fav,
      { key: 'match', items: match },
      { key: 'near', items: near },
      { key: 'all', items: rest },
    ],
    classes,
  };
}
